import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { get as _get } from "lodash";
import { Alert, Box, Breadcrumbs, Button, IconButton, Link, Tooltip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { CONFIG_BASE } from "../../constants/configs";
import { useExplorer } from "../../hooks/useExplorer";
import { useListTokens } from "../../hooks/explore";
import { explorerUrl } from "../../utils/explorer";
import shortedAddress from "../../utils/display";

/**
 * "Tokens › KOINDX": the first crumb goes back to the explore tab
 */
export const ExploreCrumbs = ({ section, href, current }) => (
  <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, "& .MuiBreadcrumbs-separator": { color: "text.secondary" } }}>
    <Link component={RouterLink} to={href} underline="hover" sx={{ color: "text.secondary" }}>
      {section}
    </Link>
    <Typography sx={{ color: "text.primary" }} aria-current="page">
      {current}
    </Typography>
  </Breadcrumbs>
);

// "koin" and "vhp" are names: the explorer needs their contract address
export const contractAddress = (id) => _get(CONFIG_BASE, ["tokens", "nameservices", id], id);

/**
 * Contract address, with copy and a link to the block explorer chosen in the settings
 */
export const AddressRow = ({ label, address }) => {
  const explorer = useExplorer();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.log(error);
    }
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
        <Typography variant="body2" title={address} sx={{ mr: 0.5 }}>
          {shortedAddress(address)}
        </Typography>
        <Tooltip title={copied ? "Copied" : "Copy address"}>
          <IconButton size="small" aria-label={`copy ${label.toLowerCase()}`} onClick={copy}>
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={`View on ${explorer.name}`}>
          <IconButton
            size="small"
            aria-label={`view ${label.toLowerCase()} on ${explorer.name}`}
            href={explorerUrl(explorer, "address", address)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const DEFAULT_SEND = _get(CONFIG_BASE, "tokens.send.address", "koin");
const DEFAULT_RECEIVED = _get(CONFIG_BASE, "tokens.received.address", "");

/**
 * Opens Swap with the given tokens; only tokens of the token list (others go through Import token and its warning)
 * The Swap page sets its tokens from ?input= and ?output= when it opens (helpers/initialize.js), so the tokens go
 * in the URL, which also makes the link shareable. from: null pays with the default of the swap (vUSDT, or KOIN to receive vUSDT)
 */
export const useStartSwap = () => {
  const navigate = useNavigate();
  const listTokens = useListTokens();
  return {
    canSwap: (...ids) => ids.every((id) => listTokens.has(id)),
    startSwap: (fromId, toId) => {
      const input = fromId || (toId === DEFAULT_SEND ? DEFAULT_RECEIVED : DEFAULT_SEND);
      navigate(`/swap?input=${encodeURIComponent(input)}&output=${encodeURIComponent(toId)}`);
    },
  };
};

export const DetailError = ({ what, onRetry }) => (
  <Alert
    severity="error"
    action={
      <Button color="inherit" size="small" onClick={onRetry}>
        Retry
      </Button>
    }
  >
    Could not load this {what} from the KoinDX API.
  </Alert>
);

// a titled block of the detail pages
export const Section = ({ title, children, sx = {} }) => (
  <Box component="section" sx={{ mt: 4, ...sx }}>
    <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);
